// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React from 'react'
import { useTranslation } from "react-i18next";
import "./ObservationLabelingPanel.scss"

import { AuthContext } from '../../context/AuthContext.js';
import { firestore } from '../../context/FirebaseConfig.js';
import { doc } from 'firebase/firestore'

import LabelingPanel from '../../shared_components/LabelingPanel/LabelingPanel.jsx';
import LabelingMotivationalPanel from '../LabelingComponents/LabelingMotivationalPanel.jsx';

import brain_icon from '../../assets/icons/gray/labelling.png';

export default function ObservationLabelingPanel({ observation, getNext, showLabelRequest = true }) {
    const { i18n, t } = useTranslation();
    const { currentUser, labelsets } = React.useContext(AuthContext);

    function getObjectType() {
        const translation = t(`object_type_${observation.object_type.replace(" ", "_")}`);
        return translation.startsWith("object_type_") ? observation.object_type : translation;
    }

    // Hotfix: if the object type is bale, we don't want to show it in the label panel
    let object_type = getObjectType();
    if (object_type === "bale") {
        object_type = "";
    }
    

    return (
        <div className="labeling-panel">
            <p className="labeling-panel--date">{observation.date}</p>
            <h1>{observation.time}</h1>
            <p>{t(object_type)}</p>
            { /* HOTFIX: Show a 'model-name' for the client if they have one (Needs to be replaced with proper model versioning!) */ }
            {currentUser.company?.temp_model_displayname &&
            <div className="model-version-display">
                <img src={brain_icon} alt="Model Version" />
                <p className="subtitle">powered by</p>
                <p className="model-name">{currentUser.company?.temp_model_displayname}</p>
            </div>}
            { /* Show predicted classes to people with flags */ }
            {currentUser.info.user_flags && ['labeler', 'admin', 'manager'].some(flag => currentUser.info.user_flags.includes(flag)) && observation.predicted_labels &&
            Object.keys(observation.predicted_labels).sort().map(labelset => {
                const predictedClass = observation?.predicted_labels[labelset]?.class;
                if (!predictedClass || !labelsets?.[labelset]?.name) return;

                // For regression models, show the numeric value
                if (labelsets[labelset]?.type === 'slider' && !isNaN(predictedClass) && typeof predictedClass === 'number') {
                    return <p className="predicted-recipe-material" key={labelset}>{`${labelsets[labelset].name}: ${Math.round(predictedClass*1000)/10}%`}</p>
                }

                // For classification models, show the predicted class name
                const predictedClassName = labelsets[labelset]?.['labels']?.[predictedClass]?.display_name ?? predictedClass;
                const accuracyFraction = observation.predicted_labels[labelset]?.cumulative_accuracy;
                const accuracy = accuracyFraction ? Math.round(accuracyFraction*100) : null;
                const accuracyString = accuracy ? `(${accuracy}%)` : '';
                return <p className="predicted-recipe-material" key={labelset}>{`${labelsets[labelset].name}: ${predictedClassName} ${accuracyString}`}</p>
            })}

            { /* Show dropdowns for labeling */ }
            {currentUser.info.user_flags && ['labeler', 'admin'].some(flag => currentUser.info.user_flags.includes(flag)) &&
            <LabelingPanel
                labelItem={observation}
                docRef={doc(firestore, 'clients', currentUser.company.company, 'observations', observation.doc_id)}
                getNext={getNext}
                currentUser={currentUser}
                labelsets={labelsets}
                firestore={firestore}
                searchableDropdowns={true}
                showLabelers={true}
                showLabelRequest={showLabelRequest}
            />
            }

            { /* Motivation Messages */}
            {currentUser.info.user_flags && ['labeler', 'admin'].some(flag => currentUser.info.user_flags.includes(flag)) && 
            <LabelingMotivationalPanel />
            }
        </div>
    )
}